import { GeometryBase, Line, Point } from "tnbase";
import { EventHandlerBase, Pointer, Primitive } from "pytha2";

interface Grid {
    bay?: string // 开间
    deep?: string // 进深
    x?: string // X坐标
    y?: string // Y坐标
}

/**
 * 快速生成轴网
 */
export class EventHandlerQuickGenAxisNet extends EventHandlerBase {

    // 生成轴网转角的normal
    private primitiveList: Primitive[] = [];

    /**
     * 处理器默认运行入口
     * @param data 
     * @returns 
     */
    run(data = {}) {
        // console.log('EventHandlerQuickGenAxisNet run');
        const grid: Grid = this.node.graph.cache.inputObject['GRID'];
        const basePoint = new Point();
        // console.log('grid', grid)

        if (!grid?.x && !grid?.y) {
            // XY坐标均没有，代表是需要放置，设置当前step为1(待移动)，basePoint在原点位置
            // 后续确定点结束的回调中修改step为2
            this.node.graph.cache.data['step'] = 1;
            basePoint.setZ(this.getPythaStorage().coordinateStore.currentPos.z);
        } else {
            // 存在XY坐标，代表不需要放置，设置当前step为2(待旋转)，basePoint在输入位置
            this.node.graph.cache.data['step'] = 2;
            // 将用户输入点作为pointer
            basePoint.setX(Number(grid?.x) || 0);
            basePoint.setY(Number(grid?.y) || 0);
            basePoint.setZ(this.getPythaStorage().coordinateStore.currentPos.z);
            let pointer = new Pointer();
            pointer.point2 = undefined;
            pointer.point = basePoint.clone();
            pointer.type = "SPECIFY";
            pointer.ctrlKey = false;
            pointer.shiftKey = false;
            pointer.altKey = false;
            pointer.button = -1;
            pointer.id = 100001;
            pointer.deltaY = 0;
            this.node.graph.cache.pointerDownList = [pointer];
        }
        this.node.graph.cache.data['basePoint'] = basePoint.clone();

        let bay = grid?.bay;
        let deep = grid?.deep;

        let bayArr: number[] = this.parseNumberArrAccumulate(bay);
        let deepArr: number[] = this.parseNumberArrAccumulate(deep);

        // console.log('bayArr', bayArr, 'deepArr', deepArr);

        // let totalBay: number = bayArr?.length > 0 ? bayArr[bayArr?.length - 1] : 3300;
        // let totalDeep: number = deepArr?.length > 0 ? deepArr[deepArr?.length - 1] : 3300;
        let totalBay: number = bayArr?.length > 0 ? bayArr[bayArr?.length - 1] : 3.3;
        let totalDeep: number = deepArr?.length > 0 ? deepArr[deepArr?.length - 1] : 3.3;

        let baseX: number = Number(basePoint.x);
        let baseY: number = Number(basePoint.y);
        let baseZ: number = this.getPythaStorage().coordinateStore.currentPos.z;

        // let axisLinePrimitives: Primitive[] = [];

        if (bayArr?.length > 0) {
            let bayY = baseY + totalDeep;

            let start = new Point(baseX, baseY, baseZ);
            let end = new Point(baseX, bayY, baseZ);
            let linePri = new Primitive("AxisLine");
            let line = new Line(start, end);
            this.addLinePrimitive(linePri, [line]);


            bayArr?.forEach((item) => {
                let start = new Point(baseX + item, baseY, baseZ);
                let end = new Point(baseX + item, bayY, baseZ);
                let linePri = new Primitive("AxisLine");
                let line = new Line(start, end);
                this.addLinePrimitive(linePri, [line]);
            })
        }
        if (deepArr?.length > 0) {
            let deepX = baseX + totalBay;

            let start = new Point(baseX, baseY, baseZ);
            let end = new Point(deepX, baseY, baseZ);
            let linePri = new Primitive("AxisLine");
            let line = new Line(start, end);
            this.addLinePrimitive(linePri, [line]);

            deepArr?.forEach((item) => {
                let start = new Point(baseX, baseY + item, baseZ);
                let end = new Point(deepX, baseY + item, baseZ);
                let linePri = new Primitive("AxisLine");
                let line = new Line(start, end);
                this.addLinePrimitive(linePri, [line]);
            })
        }
        this.getPythaStorage().selectStore.selectPrimitives = this.primitiveList;
        this.node.graph.cache.data['tempPrimitives'] = [...this.primitiveList];
        return true;
    }


    /**
    * 将数组累加
    * @param str 
    * @returns 
    */
    parseNumberArrAccumulate(str: string) {
        let lengthUnitRatio = this.getPythaStorage().coordinateStore.lengthUnitRatio;
        let parsedArray = this.parseStr2NumberArr(str);
        let res = parsedArray?.reduce((accumulator, currentValue, index) => {
            if (index === 0) {
                accumulator.push(currentValue);
            } else {
                const accumulatedValue = accumulator[index - 1] + currentValue;
                accumulator.push(accumulatedValue);
            }
            return accumulator;
        }, []);
        res = res?.map(item => item * lengthUnitRatio);
        return res;
    }

    /**
     * 将轴网数据转为可编辑数据
     * @param str 
     * @returns 
     */
    parseStr2NumberArr(str: string) {
        if (!str) {
            return [];
        }
        const parts = str.split(',');

        try {
            const result = parts.flatMap((part) => {
                const multiplicationParts = part.split('*');
                if (multiplicationParts.length === 2) {
                    const number = parseInt(multiplicationParts[0], 10);
                    const count = parseInt(multiplicationParts[1], 10);

                    if (!isNaN(number) && !isNaN(count)) {
                        return Array.from({ length: count }, () => number);
                    }
                } else {
                    const number = parseInt(part, 10);
                    if (!isNaN(number)) {
                        return [number];
                    }
                }

                return [];
            });

            return result;
        } catch (error) {
            return [];
        }
    }

    /**
     * 添加一个线图元
     * @param primitive 
     * @param geos 
     */
    addLinePrimitive(primitive: Primitive, geos: GeometryBase[]) {
        const storage = this.getPythaStorage();
        primitive.geos.push(...geos);
        let keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = [];
            geos.forEach(geo => {
                let geoElementsLength = renderItem.geoElements.length;
                geo.getGeoElementsIdxList().forEach(idx => {
                    renderItem.geoElements.push(idx + geoElementsLength);
                    // renderItem.mat.color.set(0xE54242);
                    renderItem.mat?.color.set(storage.layerStore.getColor());
                })
            });
        }

        // primitive.temporary = false;
        // storage.primitiveStore.primitiveDict[primitive.uuid] = primitive;
        primitive.temporary = true;
        primitive.layerUuid = storage.layerStore.currentLayerUuid || null;
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        primitive.needRedraw = true;
        this.node.graph.cache.primitiveList.push(primitive);
        this.primitiveList.push(primitive);
    }

}